import { Handle, Position, useUpdateNodeInternals, type NodeProps } from '@xyflow/react';
import { useContext, useEffect } from 'react';
import type { FlowGraphNodeParameter, ModuleNodeData, ModuleParameter } from '../types';
import { exitLabel, exitLetter } from '../exitLabels';
import { LookupCtx } from '../LookupContext';
import { lookupKindForType, type LookupKind } from '../lookups';
import { isExitActive } from '../exitVisibility';
import { storedToHHMM } from './ParamEditor';

const NODE_WIDTH = 200;
/** Rendered width of a module node (px, flow units); App uses it to anchor new nodes. */
export const MODULE_NODE_WIDTH = NODE_WIDTH;

// Custom React Flow node that mirrors the POC card design:
//  - colored bar + module icon + title in the header
//  - module type pill on the right
//  - dynamic body summarising key configured parameters
//  - 1 input handle (top) + 1 output handle per ModuleExit (bottom)
export function ModuleNode(props: NodeProps) {
  const data = props.data as unknown as ModuleNodeData;
  const def = data.moduleDef;
  const color = def?.uiColor || '#94A3B8';
  // Visible exits = module default-visible OR explicitly activated on this
  // node OR currently connected (hiding a connected exit would orphan the
  // edge). Everything else is surfaced as "Not used" in the properties panel.
  const allExits = def?.exits ?? [];
  const exits = allExits.filter((e) => {
    if (data.exitTargets && data.exitTargets[e.exitId]) return true;
    return isExitActive(e, data.exitStates?.[e.exitId]);
  });
  const hasInput = !(def?.categoryName?.toLowerCase() === 'trigger' || def?.name?.toLowerCase().includes('trigger'));
  const summary = useBuildSummary(data);

  // React Flow caches handle positions per <Handle> the first time it sees
  // them in the DOM. When we add/remove handles dynamically (because exits
  // are activated/deactivated, or because a different exit became
  // connected and is now visible) the cached coordinates would be off,
  // making freshly-drawn edges start from stale positions. We pass a
  // signature of the visible-handle set + their layout-determining inputs
  // to useUpdateNodeInternals so React Flow re-measures the DOM whenever
  // the signature changes.
  const updateNodeInternals = useUpdateNodeInternals();
  const handleSignature = exits.map((e) => e.exitId).join(',');
  useEffect(() => {
    updateNodeInternals(props.id);
  }, [props.id, handleSignature, updateNodeInternals]);

  return (
    <div
      className={`fb-node ${props.selected ? 'sel' : ''} ${data.isInitial ? 'fb-node--initial' : ''}`}
      style={{ width: NODE_WIDTH }}
    >
      {hasInput && (
        <Handle
          type="target"
          position={Position.Top}
          id="in"
          className="fb-handle fb-handle--in"
        />
      )}

      <div className="fb-node-hd">
        <span className="fb-node-bar" style={{ background: color }} />
        <span className="fb-node-icon">{renderIcon(def?.icon)}</span>
        <div className="fb-node-info">
          <div className="fb-node-title">{data.label || def?.name || 'Untitled'}</div>
          {data.isInitial && <div className="fb-node-badge">START</div>}
        </div>
        <span className="fb-node-type" style={{ color }}>
          {def?.name || `module ${data.moduleId}`}
        </span>
      </div>

      <div className="fb-node-body">{summary}</div>

      {exits.length === 0 && (
        <div className="fb-node-foot">
          <span className="fb-node-foot-lbl">no outputs</span>
        </div>
      )}

      {(() => {
        // Render the catalog exits as a single bottom row.
        const totalCount = exits.length;
        const handlePos = (i: number) => ((i + 1) / (totalCount + 1)) * 100;
        return (
          <>
            {exits.map((exit, i) => {
              const left = handlePos(i);
              const letter = exitLetter(exit.name);
              const target = data.exitTargets?.[exit.exitId];
              const fullName = exitLabel(exit.name);
              const tooltip = target
                ? `${fullName} → ${target.name}`
                : `${fullName} (unconnected)`;
              return (
                <Handle
                  key={`exit-${exit.exitId}`}
                  type="source"
                  position={Position.Bottom}
                  id={String(exit.exitId)}
                  className={`fb-handle fb-handle--out${target ? ' fb-handle--connected' : ''}`}
                  style={{ left: `${left}%` }}
                  title={tooltip}
                >
                  <span className="fb-handle-letter">{letter}</span>
                </Handle>
              );
            })}
          </>
        );
      })()}
    </div>
  );
}

/**
 * Looks up the currently-stored Recognizer parameter value on a node.
 * Returns the RecognizerKey (string) or null when the node has no
 * Recognizer parameter or the user hasn't picked one yet.
 */
export function findRecognizerValue(
  defParams: ReadonlyArray<ModuleParameter>,
  nodeParams: Record<number, FlowGraphNodeParameter>,
): string | null {
  for (const p of defParams) {
    if ((p.typeName ?? '').trim().toLowerCase() !== 'recognizer') continue;
    const cur = nodeParams[p.parameterId];
    const v = (cur?.value ?? p.defaultValue ?? '').trim();
    return v || null;
  }
  return null;
}

// Summary rows shown in the node body. Rules:
//  - Every parameter that has a non-empty value is shown.
//  - Bit (TypeId 8) parameters are ALWAYS shown, rendered as "Yes" / "No".
//  - Divider (TypeId 14) parameters are skipped (purely cosmetic in the form).
//  - Hidden (TypeId 15) parameters are masked.
//  - KeyValuePair (TypeId 18) values are unwrapped to show the picked label.
//  - Voice / Huntgroup / Agent / Weekplan / Calendar / Flow / Application /
//    Variable ids are resolved to their human-readable name via the lookup
//    context (falling back to the raw id if the lookup hasn't loaded yet).
function useBuildSummary(data: ModuleNodeData) {
  const resolvers = useContext(LookupCtx);
  const def = data.moduleDef;
  if (!def || def.parameters.length === 0) {
    return <span className="fb-muted">no parameters</span>;
  }
  // Match the PropertiesPanel ordering: by sortOrder (asc) with parameterId
  // as a stable tie-breaker, so the node summary reads top-to-bottom in the
  // same sequence the editor shows the fields.
  const ordered = def.parameters.slice().sort((a, b) => {
    const av = a.sortOrder ?? Number.POSITIVE_INFINITY;
    const bv = b.sortOrder ?? Number.POSITIVE_INFINITY;
    if (av !== bv) return av - bv;
    return a.parameterId - b.parameterId;
  });
  const rows = ordered
    .filter((p) => p.typeId !== 14) // skip dividers
    .map((p) => {
      const cur = data.parameters[p.parameterId];
      const display = formatSummaryValue(p, cur, resolvers);
      if (display == null) return null;
      return { p, display };
    })
    .filter((x): x is { p: typeof def.parameters[number]; display: SummaryValue } => x !== null);

  if (rows.length === 0) {
    return <span className="fb-muted">not configured</span>;
  }
  return (
    <>
      {rows.map(({ p, display }) => {
        if (typeof display === 'string') {
          return (
            <div key={p.parameterId} className="fb-summary-row">
              <span className="fb-summary-key">{p.name}:</span>
              <span className="fb-summary-val">{trim(display)}</span>
            </div>
          );
        }
        // Structured value: a parent row (with optional primary) followed
        // by indented sub-rows. Each sub-row is its own line so the user
        // can scan the actual variable / column names at a glance, just
        // like they would in the legacy designer.
        return (
          <div key={p.parameterId} className="fb-summary-group">
            <div className="fb-summary-row">
              <span className="fb-summary-key">{p.name}:</span>
              {display.primary && (
                <span className="fb-summary-val">{trim(display.primary)}</span>
              )}
            </div>
            {display.items.map((item, idx) => (
              <div key={idx} className="fb-summary-subrow">
                {item.label && (
                  <span className="fb-summary-sublbl">{item.label}</span>
                )}
                <span className="fb-summary-val">{trim(item.value, 32)}</span>
              </div>
            ))}
          </div>
        );
      })}
    </>
  );
}

interface SummaryItem {
  label?: string;
  value: string;
}

interface SummaryGroup {
  primary?: string;
  items: SummaryItem[];
}

type SummaryValue = string | SummaryGroup;

// Returns the display value for the summary, or `null` to skip the row.
// Most parameter types return a plain string; complex types (DB lookups,
// variable lists) return a `SummaryGroup` so each child mapping/variable
// gets its own line — mirroring how the legacy designer surfaces them
// in the node body.
function formatSummaryValue(
  p: ModuleParameter,
  cur: FlowGraphNodeParameter | undefined,
  resolvers: Partial<Record<LookupKind, Map<string, string>>>
): SummaryValue | null {
  const raw = cur?.value;
  const kind = (cur?.valueType || '').toLowerCase();
  // Bit: always show as Yes/No (default No when unset).
  if (p.typeId === 8) {
    const on = raw === '1' || raw?.toLowerCase() === 'true';
    return on ? 'Yes' : 'No';
  }
  if (raw == null || raw === '') return null;
  // Hidden / passwords: mask.
  if (p.typeId === 15) return '••••';
  // Time-of-day: stored as minutes since 00:00, render as HH:MM.
  if (p.typeId === 20 || (p.typeName || '').toLowerCase() === 'time') {
    const hhmm = storedToHHMM(raw);
    if (hhmm) return hhmm;
  }
  // KeyValuePair: unwrap to picked label.
  if (p.typeId === 18) {
    try {
      const obj = JSON.parse(raw) as { value?: string; options?: Record<string, string> };
      if (obj && obj.value != null && obj.options && obj.options[obj.value]) {
        return obj.options[obj.value];
      }
    } catch {
      // fall through and show raw
    }
  }
  // Data Lookup (TypeId 13) / Schedule Lookup (TypeId 21): show the
  // database name on the parent row, then one sub-row per column mapping
  // ("col_3 → $VAR.X") so the user can scan the actual configuration
  // from the canvas without opening the properties panel.
  if (p.typeId === 13 || p.typeId === 21) {
    try {
      const obj = JSON.parse(raw) as { simpleDbId?: number | string; dbName?: string } & Record<string, unknown>;
      if (obj && typeof obj === 'object') {
        const idResolver = resolvers.database;
        const resolved = obj.simpleDbId != null ? idResolver?.get(String(obj.simpleDbId)) : null;
        const dbName = obj.dbName || resolved || (obj.simpleDbId != null ? `db ${obj.simpleDbId}` : null);
        const items: SummaryItem[] = [];
        for (let i = 1; i <= 10; i++) {
          const v = obj[`col_${i}`];
          if (v == null || v === '') continue;
          const varName = resolvers.variable?.get(String(v)) ?? String(v);
          items.push({ label: `col_${i}`, value: varName });
        }
        if (dbName || items.length > 0) {
          return { primary: dbName ?? undefined, items };
        }
      }
    } catch {
      // fall through and show raw
    }
  }
  // Slot mapping (Mod_VB_Ask SlotMap): `{ slotKey: varId, __confidence__: varId }`
  // — one sub-row per mapped slot ("plaats → $VAR.X"), id resolved to a name.
  if (kind === 'slotmapping') {
    try {
      const obj = JSON.parse(raw) as Record<string, unknown> | null;
      if (obj == null || typeof obj !== 'object') return 'no slots mapped';
      const items: SummaryItem[] = [];
      for (const [slotKey, v] of Object.entries(obj)) {
        if (v == null || v === '') continue;
        const label = slotKey === '__confidence__' ? 'confidence' : slotKey;
        items.push({ label, value: resolvers.variable?.get(String(v)) ?? String(v) });
      }
      return items.length > 0 ? { items } : 'no slots mapped';
    } catch {
      // fall through and show raw
    }
  }
  // Save Variables (TypeId 17) / XML-RPC Mappings (TypeId 22): both store
  // `{id_1, id_2, ...}` — list every variable on its own sub-row, with
  // the id resolved to a human-readable variable name.
  if (p.typeId === 17 || p.typeId === 22 || kind === 'variablelist') {
    try {
      // Save-Variables persists "null" (literal JSON null) for empty lists.
      const obj = JSON.parse(raw) as Record<string, unknown> | null;
      if (obj == null || typeof obj !== 'object') return 'no variables';
      const ids: string[] = [];
      for (let i = 1; ; i++) {
        const v = obj[`id_${i}`];
        if (v == null || v === '') break;
        ids.push(String(v));
      }
      if (ids.length === 0) return 'no variables';
      const items: SummaryItem[] = ids.map((id) => ({
        value: resolvers.variable?.get(id) ?? id,
      }));
      return { items };
    } catch {
      // fall through and show raw
    }
  }
  // Variable reference: resolve via the variable list. Variable names in
  // the DB already start with "$" (e.g. "$CALL.CALLED_NUMBER") so we don't
  // add a prefix here — otherwise we end up with "$$CALL.CALLED_NUMBER".
  if (kind === 'variable') {
    const name = resolvers.variable?.get(raw);
    return name ?? raw;
  }
  // Resolve id->name via the lookup for the stored kind (or the parameter's
  // default kind when the DB left valueType null).
  const lookupKind =
    lookupKindForType(kind) ?? lookupKindForType(p.typeName) ?? null;
  if (lookupKind) {
    const name = resolvers[lookupKind]?.get(raw);
    if (name) return name;
  }
  return raw;
}

function trim(v: string, max = 28) {
  if (v.length <= max) return v;
  return v.substring(0, max) + '…';
}

// Icon support: stored as Font Awesome class name (matches existing portal usage).
// We render either an <i class="fa..."/> or a fallback unicode glyph if it's empty.
function renderIcon(icon: string | null | undefined) {
  if (!icon) return '⬜';
  const trimmed = icon.trim();
  if (trimmed.startsWith('fa') || trimmed.includes('fa-')) {
    return <i className={trimmed} aria-hidden="true" />;
  }
  return trimmed;
}
